import { useCallback, useEffect, useState } from 'react';
import { createQuizRevisionConversation, getQuizRevisionConversation } from '../services/quizRevisionService';

/**
 * Hook to fetch conversationId for a single quiz question
 * Falls back to creating the conversation if lookup returns 404 or 500 (direct URL access case)
 * @param {string} quizJobGuid - Quiz job GUID
 * @param {string} questionReferenceId - Question reference ID (same as questionGuid)
 * @param {string} userId - User ID for fallback creation
 * @param {Object} quizQuestionData - Quiz question data for fallback creation
 * @returns {Object} { conversationId, isLoading, error, refetch }
 */
export function useQuizRevisionConversation(quizJobGuid, questionReferenceId, userId = null, quizQuestionData = null) {
  const [conversationId, setConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConversation = useCallback(async () => {
    if (!quizJobGuid || !questionReferenceId) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getQuizRevisionConversation(quizJobGuid, questionReferenceId);
      setConversationId(response.conversationId);
    } catch (err) {
      // 404 or 500 means no conversation exists - try fallback creation for direct URL access
      // Backend may return 500 instead of 404 in some cases when conversation doesn't exist
      if (err.statusCode === 404 || err.statusCode === 500) {
        // Fallback: try to create conversation on-demand (for direct URL access)
        if (userId && quizQuestionData) {
          try {
            const createResponse = await createQuizRevisionConversation({
              externalUserId: userId,
              quizJobGuid,
              questionReferenceId,
              quizQuestionData,
              learningObjectives: {}, // Empty for direct access - LOs not available
            });
            setConversationId(createResponse.conversationId);
          } catch (createErr) {
            console.error('[useQuizRevisionConversation] Fallback creation failed:', createErr);
            setError(createErr);
          }
        } else {
          // No fallback data available - set error to inform user
          setConversationId(null);
          setError(err);
        }
      } else {
        console.error('[useQuizRevisionConversation] Error fetching conversation:', err);
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [quizJobGuid, questionReferenceId, userId, quizQuestionData]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  return {
    conversationId,
    isLoading,
    error,
    refetch: fetchConversation,
  };
}

export default useQuizRevisionConversation;
